// Generic creator-workspace layer: editable entities, draft workspaces,
// workspace conversations, and their row mappers, shared by every Creator
// entity type. World-only flows (proposals, revisions, publish) live in
// world-workspaces.ts; the legacy creator-session vocabulary lives in
// creator-sessions.ts. Draft payloads are opaque here: Creator and World
// Authoring own their respective payload semantics.
import {
  supabase, uid,
  isRecord, toStringValue, toNullableString, toNumberValue, toNullableNumber,
  toRecord, toRecordArray, toUnknownList,
} from './shared'
import type { RecordLike } from './shared'
import { mapWorldVersionRow } from './worlds'
import type {
  CreatorEntityType,
  CreatorSession,
  CreatorWorkspaceBundle,
  CreatorWorkspaceSummary,
  DraftProposal,
  DraftRevision,
  DraftWorkspace,
  EditableEntity,
  WorkspaceConversation,
  WorkspaceConversationEvent,
  WorkspaceConversationMessage,
  WorldWorkspaceBundle,
} from '@/types/domain'
import type {
  CreatorWorkspaceDraftUpdateResponse,
  CreatorWorkspaceCommitRequest,
  CreatorWorkspaceCommitResponse,
  CreatorWorkspaceEnsureRequest,
  CreatorWorkspaceFinalizeResponse,
  CreatorWorkspaceLoadResponse,
  WorldConversationRewindRequest,
  WorldConversationRewindResponse,
} from '@/types/api'
import type {
  CreatorWorkspaceSummariesRequest,
  WorkspaceConversationEventCreateData,
  WorkspaceConversationMessageCreateData,
  WorkspaceConversationPromptSettingsRequest,
} from '../../contract'

const CREATOR_ENTITY_TYPES = new Set(['character', 'lorebook', 'world']);
const CREATOR_SESSION_STATUSES = new Set(['draft', 'saved']);

export function normalizeCreatorEntityType(value: string): string {
  return CREATOR_ENTITY_TYPES.has(value) ? value : 'character';
}

export function normalizeCreatorSessionStatus(value: string): string {
  return CREATOR_SESSION_STATUSES.has(value) ? value : 'draft';
}

export function buildDefaultCreatorTitle(entityType: string): string {
  if (entityType === 'lorebook') return 'Untitled Lorebook Draft';
  if (entityType === 'world') return 'Untitled World Draft';
  return 'Untitled Character Draft';
}

export function mapEditableEntity(row: unknown): EditableEntity | null {
  if (!isRecord(row)) return null
  const entityType = ['world', 'character', 'lorebook'].includes(String(row.entity_type))
    ? row.entity_type as CreatorEntityType
    : 'character'
  return {
    id: toNumberValue(row.id),
    entity_type: entityType,
    entity_id: toNullableNumber(row.entity_id),
    owner_id: toStringValue(row.owner_id),
    created_at: toStringValue(row.created_at),
    updated_at: toStringValue(row.updated_at),
  }
}

export function mapDraftWorkspace(row: unknown): DraftWorkspace | null {
  if (!isRecord(row)) return null
  return {
    id: toNumberValue(row.id),
    editable_entity_id: toNumberValue(row.editable_entity_id),
    status: row.status === 'archived' ? 'archived' : 'active',
    current_revision: toNumberValue(row.current_revision),
    current_payload: toRecord(row.current_payload),
    creator_guidance: toRecord(row.creator_guidance),
    created_from_version_id: toNullableNumber(row.created_from_version_id),
    title: toStringValue(row.title),
    created_at: toStringValue(row.created_at),
    updated_at: toStringValue(row.updated_at),
  }
}

export function mapDraftRevision(row: unknown): DraftRevision {
  const safeRow = toRecord(row)
  return {
    id: toNumberValue(safeRow.id),
    workspace_id: toNumberValue(safeRow.workspace_id),
    revision: toNumberValue(safeRow.revision),
    payload: toRecord(safeRow.payload),
    source_type: toStringValue(safeRow.source_type),
    source_id: toNullableNumber(safeRow.source_id),
    created_at: toStringValue(safeRow.created_at),
  }
}

export function mapWorkspaceConversation(row: unknown): WorkspaceConversation | null {
  if (!isRecord(row)) return null
  return {
    id: toNumberValue(row.id),
    workspace_id: toNumberValue(row.workspace_id),
    bot_id: toNullableNumber(row.bot_id),
    advanced_prompt_enabled: Boolean(row.advanced_prompt_enabled),
    prompt_setup: toRecord(row.prompt_setup),
    title: toStringValue(row.title),
    status: row.status === 'archived' ? 'archived' : 'active',
    legacy_creator_session_id: toNullableNumber(row.legacy_creator_session_id),
    created_at: toStringValue(row.created_at),
    updated_at: toStringValue(row.updated_at),
  }
}

export function mapWorkspaceMessage(row: unknown): WorkspaceConversationMessage {
  const safeRow = toRecord(row)
  const status = ['pending', 'streaming', 'complete', 'failed', 'aborted'].includes(String(safeRow.status))
    ? safeRow.status as WorkspaceConversationMessage['status']
    : 'complete'
  const role = ['user', 'assistant', 'system', 'tool'].includes(String(safeRow.role))
    ? safeRow.role as WorkspaceConversationMessage['role']
    : 'user'
  return {
    id: toNumberValue(safeRow.id) || toStringValue(safeRow.id),
    conversation_id: toNumberValue(safeRow.conversation_id),
    role,
    content: toStringValue(safeRow.content),
    turn_id: toNullableString(safeRow.turn_id),
    stream_id: toNullableString(safeRow.stream_id),
    status,
    created_at: toStringValue(safeRow.created_at),
    updated_at: toStringValue(safeRow.updated_at),
    t: toStringValue(safeRow.created_at),
  }
}

export function mapWorkspaceEvent(row: unknown): WorkspaceConversationEvent {
  const safeRow = toRecord(row)
  return {
    id: toNumberValue(safeRow.id),
    conversation_id: toNumberValue(safeRow.conversation_id),
    event_type: ['tool_call', 'tool_result', 'approval', 'rejection', 'summary'].includes(String(safeRow.event_type))
      ? safeRow.event_type as WorkspaceConversationEvent['event_type']
      : 'summary',
    parent_event_id: toNullableNumber(safeRow.parent_event_id),
    turn_id: toNullableString(safeRow.turn_id),
    tool_name: toNullableString(safeRow.tool_name),
    payload: toRecord(safeRow.payload),
    created_at: toStringValue(safeRow.created_at),
  }
}

export function mapDraftProposal(row: unknown): DraftProposal {
  const safeRow = toRecord(row)
  const status = ['pending', 'approved', 'rejected', 'superseded', 'invalid'].includes(String(safeRow.status))
    ? safeRow.status as DraftProposal['status']
    : 'pending'
  return {
    id: toNumberValue(safeRow.id),
    workspace_id: toNumberValue(safeRow.workspace_id),
    origin_conversation_id: toNullableNumber(safeRow.origin_conversation_id),
    origin_turn_id: toNullableString(safeRow.origin_turn_id),
    base_revision: toNumberValue(safeRow.base_revision),
    operations: toRecordArray(safeRow.operations),
    status,
    created_at: toStringValue(safeRow.created_at),
    resolved_at: toNullableString(safeRow.resolved_at),
    resolution_reason: toStringValue(safeRow.resolution_reason),
  }
}

export function mapCreatorWorkspaceBundle(data: unknown): CreatorWorkspaceBundle {
  const payload = toRecord(data)
  return {
    editable_entity: mapEditableEntity(payload.editable_entity),
    workspace: mapDraftWorkspace(payload.workspace),
    conversations: toRecordArray(payload.conversations).map(mapWorkspaceConversation).filter(Boolean) as WorkspaceConversation[],
    conversation: mapWorkspaceConversation(payload.conversation),
    messages: toRecordArray(payload.messages).map(mapWorkspaceMessage),
    events: toRecordArray(payload.events).map(mapWorkspaceEvent),
    revisions: toRecordArray(payload.revisions).map(mapDraftRevision),
  }
}

export function mapWorldWorkspaceBundle(data: unknown): WorldWorkspaceBundle {
  const payload = toRecord(data)
  return {
    ...mapCreatorWorkspaceBundle(payload),
    proposals: toRecordArray(payload.proposals).map(mapDraftProposal),
    active_version: payload.active_version ? mapWorldVersionRow(payload.active_version) : null,
    world_versions: toRecordArray(payload.world_versions).map(mapWorldVersionRow),
  }
}

function mapCreatorLoad(data: unknown): CreatorWorkspaceLoadResponse {
  const payload = toRecord(data)
  return {
    editable_entity: mapEditableEntity(payload.editable_entity),
    workspace: mapDraftWorkspace(payload.workspace),
    conversations: toRecordArray(payload.conversations).map(mapWorkspaceConversation).filter(Boolean) as WorkspaceConversation[],
    conversation: mapWorkspaceConversation(payload.conversation),
    messages: toRecordArray(payload.messages).map(mapWorkspaceMessage),
    events: toRecordArray(payload.events).map(mapWorkspaceEvent),
  }
}

function mapSummaryRow(row: unknown): CreatorWorkspaceSummary | null {
  if (!isRecord(row)) return null
  const entityType = normalizeCreatorEntityType(toStringValue(row.entity_type)) as CreatorEntityType
  return {
    workspace_id: toNumberValue(row.workspace_id),
    active_conversation_id: row.active_conversation_id == null ? null : toNumberValue(row.active_conversation_id),
    entity_type: entityType,
    linked_entity_id: row.linked_entity_id == null ? null : toNumberValue(row.linked_entity_id),
    status: normalizeCreatorSessionStatus(toStringValue(row.status)) as CreatorSession['status'],
    title: toStringValue(row.title),
    // Entity features own the meaning of this free-form payload. The workspace
    // adapter only guarantees that consumers receive a record.
    draft_payload: toRecord(row.draft_display),
    has_unsaved_changes: Boolean(row.has_unsaved_changes),
    conversation_count: toNumberValue(row.conversation_count),
    created_at: toStringValue(row.created_at),
    updated_at: toStringValue(row.updated_at),
  }
}

export async function getWorkspaceById(id: number | string): Promise<DraftWorkspace | null> {
  const { data, error } = await supabase
    .from('draft_workspaces')
    .select('*')
    .eq('id', Number(id))
    .eq('status', 'active')
    .maybeSingle()
  if (error) throw error
  return mapDraftWorkspace(data)
}

export async function getConversationById(id: number | string): Promise<WorkspaceConversation | null> {
  const { data, error } = await supabase
    .from('workspace_conversations')
    .select('*')
    .eq('id', Number(id))
    .eq('status', 'active')
    .maybeSingle()
  if (error) throw error
  return mapWorkspaceConversation(data)
}

// Legacy compat: old creator-session routes carried the conversation id (or a
// pre-workspace session id) instead of the workspace id.
async function getConversationByCompatId(id: number | string): Promise<WorkspaceConversation | null> {
  const numericId = Number(id)
  const byId = await supabase
    .from('workspace_conversations')
    .select('*')
    .eq('id', numericId)
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle()
  if (byId.error) throw byId.error
  const direct = mapWorkspaceConversation(byId.data)
  if (direct) return direct

  const byLegacy = await supabase
    .from('workspace_conversations')
    .select('*')
    .eq('legacy_creator_session_id', numericId)
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle()
  if (byLegacy.error) throw byLegacy.error
  return mapWorkspaceConversation(byLegacy.data)
}

export async function getCreatorWorkspaceSummaries(request: CreatorWorkspaceSummariesRequest = {}) {
  const entityTypes = request?.entity_type
    ? [normalizeCreatorEntityType(request.entity_type)]
    : null
  const status = request?.status
    ? normalizeCreatorSessionStatus(request.status)
    : null

  const { data, error } = await supabase.rpc('rpc_list_creator_workspace_summaries_v1', {
    p_entity_types: entityTypes,
    p_status: status,
  })
  if (error) throw error

  return {
    creator_workspaces: toUnknownList(data)
      .map(mapSummaryRow)
      .filter((summary): summary is CreatorWorkspaceSummary => Boolean(summary)),
  }
}

export async function resolveCreatorWorkspaceRoute(id: number | string) {
  const workspace = await getWorkspaceById(id)
  if (workspace) {
    return {
      workspace_id: workspace.id,
      conversation_id: null,
      needs_redirect: false,
    }
  }

  const conversation = await getConversationByCompatId(id)
  if (!conversation) throw new Error('Creator workspace not found')
  return {
    workspace_id: conversation.workspace_id,
    conversation_id: conversation.id,
    needs_redirect: true,
  }
}

export async function listWorkspaceConversations(workspaceId: number | string) {
  const { data, error } = await supabase
    .from('workspace_conversations')
    .select('*')
    .eq('workspace_id', Number(workspaceId))
    .eq('status', 'active')
    .order('updated_at', { ascending: false })
    .order('id', { ascending: false })
  if (error) throw error
  return {
    conversations: toUnknownList(data)
      .map(mapWorkspaceConversation)
      .filter((conversation): conversation is WorkspaceConversation => Boolean(conversation)),
  }
}

export async function getCreatorWorkspace(
  workspaceId: number | string,
  selectedConversationId?: number | string | null,
): Promise<CreatorWorkspaceBundle> {
  const { data, error } = await supabase.rpc('creator_workspace_bundle', {
    p_workspace_id: Number(workspaceId),
    p_selected_conversation_id: toNullableNumber(selectedConversationId),
  })
  if (error) throw error
  return mapCreatorWorkspaceBundle(data)
}

export async function getCreatorWorkspaceLoad(
  workspaceId: number | string,
  selectedConversationId?: number | string | null,
): Promise<CreatorWorkspaceLoadResponse> {
  const { data, error } = await supabase.rpc('rpc_creator_workspace_load_v1', {
    p_workspace_id: Number(workspaceId),
    p_selected_conversation_id: toNullableNumber(selectedConversationId),
  })
  if (error) throw error
  return mapCreatorLoad(data)
}

export async function ensureCreatorWorkspace(
  entityType: CreatorEntityType,
  linkedEntityId: number | string | null = null,
  seedPayload: RecordLike = {},
  title = '',
  request: Omit<CreatorWorkspaceEnsureRequest, 'entity_type' | 'linked_entity_id' | 'seed_payload' | 'title'> = {},
): Promise<CreatorWorkspaceBundle> {
  const { data, error } = await supabase.rpc('rpc_ensure_creator_workspace_v2', {
    p_entity_type: entityType,
    p_linked_entity_id: toNullableNumber(linkedEntityId),
    p_seed_payload: seedPayload,
    p_title: title,
    p_bot_id: request.bot_id ?? null,
    p_advanced_prompt_enabled: Boolean(request.advanced_prompt_enabled),
    p_creator_guidance: request.creator_guidance ?? {},
  })
  if (error) throw error
  return mapCreatorWorkspaceBundle(data)
}

export async function createCreatorWorkspaceConversation(
  workspaceId: number | string,
  cloneFromConversationId?: number | string | null,
  title = '',
): Promise<CreatorWorkspaceBundle> {
  const { data, error } = await supabase.rpc('rpc_create_creator_workspace_conversation_v2', {
    p_workspace_id: Number(workspaceId),
    p_clone_from_conversation_id: toNullableNumber(cloneFromConversationId),
    p_title: toNullableString(title),
  })
  if (error) throw error
  return mapCreatorWorkspaceBundle(data)
}

export async function commitCreatorWorkspace(
  request: CreatorWorkspaceCommitRequest,
): Promise<CreatorWorkspaceCommitResponse> {
  const { data, error } = await supabase.rpc('rpc_commit_creator_workspace_v1', {
    p_workspace_id: request.workspace_id,
    p_conversation_id: request.conversation_id,
    p_expected_revision: request.expected_revision,
    p_draft_payload: request.draft_payload,
    p_creator_guidance: request.creator_guidance,
    p_title: request.title || null,
    p_events: request.events ?? [],
  })
  if (error) throw error
  const payload = toRecord(data)
  return {
    workspace: mapDraftWorkspace(payload.workspace)!,
    revision: payload.revision ? mapDraftRevision(payload.revision) : null,
    events: toRecordArray(payload.events).map(mapWorkspaceEvent),
  }
}

export async function updateCreatorWorkspaceDraft(
  workspaceId: number | string,
  draftPayload: RecordLike = {},
  title = '',
): Promise<CreatorWorkspaceDraftUpdateResponse> {
  const { data, error } = await supabase.rpc('rpc_update_creator_workspace_draft_v1', {
    p_workspace_id: Number(workspaceId),
    p_draft_payload: draftPayload,
    p_title: title ? title : null,
  })
  if (error) throw error
  const payload = toRecord(data)
  return {
    workspace: mapDraftWorkspace(payload.workspace)!,
    revision: payload.revision ? mapDraftRevision(payload.revision) : null,
  }
}

export async function finalizeCreatorWorkspace(
  workspaceId: number | string,
): Promise<CreatorWorkspaceFinalizeResponse> {
  const { data, error } = await supabase.rpc('rpc_finalize_creator_workspace_v2', {
    p_workspace_id: Number(workspaceId),
  })
  if (error) throw error
  const payload = toRecord(data)
  return {
    editable_entity: mapEditableEntity(payload.editable_entity)!,
    workspace: mapDraftWorkspace(payload.workspace)!,
    entity_type: ['world', 'character', 'lorebook'].includes(String(payload.entity_type))
      ? payload.entity_type as CreatorEntityType
      : 'character',
    entity_id: toNumberValue(payload.entity_id),
  }
}

export async function deleteCreatorWorkspace(workspaceId: number | string) {
  const workspace = await getWorkspaceById(workspaceId)
  if (!workspace) return { message: 'Creator workspace deleted successfully' }

  const { data: conversationRows, error: conversationsError } = await supabase
    .from('workspace_conversations')
    .select('*')
    .eq('workspace_id', workspace.id)
  if (conversationsError) throw conversationsError
  const conversationIds = toUnknownList(conversationRows)
    .map(mapWorkspaceConversation)
    .filter((conversation): conversation is WorkspaceConversation => Boolean(conversation))
    .map((conversation) => Number(conversation.id))

  if (conversationIds.length) {
    const { error: messagesError } = await supabase
      .from('conversation_messages')
      .delete()
      .in('conversation_id', conversationIds)
    if (messagesError) throw messagesError

    const { error: eventsError } = await supabase
      .from('conversation_events')
      .delete()
      .in('conversation_id', conversationIds)
    if (eventsError) throw eventsError

    const { error: conversationDeleteError } = await supabase
      .from('workspace_conversations')
      .delete()
      .eq('workspace_id', workspace.id)
    if (conversationDeleteError) throw conversationDeleteError
  }

  const { error: revisionsError } = await supabase
    .from('draft_revisions')
    .delete()
    .eq('workspace_id', workspace.id)
  if (revisionsError) throw revisionsError

  const { data: entityRow, error: entityError } = await supabase
    .from('editable_entities')
    .select('*')
    .eq('id', workspace.editable_entity_id)
    .maybeSingle()
  if (entityError) throw entityError
  const entity = mapEditableEntity(entityRow)

  const { error: workspaceError } = await supabase
    .from('draft_workspaces')
    .delete()
    .eq('id', workspace.id)
  if (workspaceError) throw workspaceError

  if (entity && entity.entity_id == null) {
    const { data: siblingRows, error: siblingError } = await supabase
      .from('draft_workspaces')
      .select('id')
      .eq('editable_entity_id', entity.id)
      .eq('status', 'active')
    if (siblingError) throw siblingError
    if (!toUnknownList(siblingRows).length) {
      const { error: deleteEntityError } = await supabase
        .from('editable_entities')
        .delete()
        .eq('id', entity.id)
      if (deleteEntityError) throw deleteEntityError
    }
  }

  return { message: 'Creator workspace deleted successfully' }
}

export async function setWorkspaceConversationBot(conversationId: number | string, botId: number | null) {
  const { data, error } = await supabase
    .from('workspace_conversations')
    .update({ bot_id: botId, updated_at: new Date().toISOString() })
    .eq('id', Number(conversationId))
    .select('*')
    .single()
  if (error) throw error
  return { conversation: mapWorkspaceConversation(data)! }
}

export async function setWorkspaceConversationPromptSettings(
  conversationId: number | string,
  request: WorkspaceConversationPromptSettingsRequest = {},
) {
  const payload: RecordLike = { updated_at: new Date().toISOString() }
  if (request.advanced_prompt_enabled !== undefined) {
    payload.advanced_prompt_enabled = Boolean(request.advanced_prompt_enabled)
  }
  if (request.prompt_setup !== undefined) {
    payload.prompt_setup = request.prompt_setup
  }

  const { data, error } = await supabase
    .from('workspace_conversations')
    .update(payload)
    .eq('id', Number(conversationId))
    .select('*')
    .single()
  if (error) throw error
  return { conversation: mapWorkspaceConversation(data)! }
}

export async function renameWorkspaceConversation(conversationId: number | string, title: string) {
  const { data, error } = await supabase
    .from('workspace_conversations')
    .update({ title: title.trim(), updated_at: new Date().toISOString() })
    .eq('id', Number(conversationId))
    .select('*')
    .single()
  if (error) throw error
  return { conversation: mapWorkspaceConversation(data)! }
}

export async function deleteWorkspaceConversation(conversationId: number | string) {
  const { error } = await supabase
    .from('workspace_conversations')
    .delete()
    .eq('id', Number(conversationId))
  if (error) throw error
  return { id: Number(conversationId) }
}

export async function createWorkspaceConversationMessage(
  conversationId: number | string,
  request: WorkspaceConversationMessageCreateData,
) {
  const { data, error } = await supabase
    .from('conversation_messages')
    .insert({
      conversation_id: Number(conversationId),
      role: request.role,
      content: request.content,
      turn_id: toNullableString(request.turn_id),
      stream_id: toNullableString(request.stream_id),
      status: request.status ?? 'complete',
    })
    .select('*')
    .single()
  if (error) throw error
  return { message: mapWorkspaceMessage(data) }
}

export async function createWorkspaceConversationEvent(
  conversationId: number | string,
  request: WorkspaceConversationEventCreateData,
) {
  const { data, error } = await supabase
    .from('conversation_events')
    .insert({
      conversation_id: Number(conversationId),
      event_type: request.event_type,
      parent_event_id: request.parent_event_id ?? null,
      turn_id: toNullableString(request.turn_id),
      tool_name: toNullableString(request.tool_name),
      payload: request.payload ?? {},
    })
    .select('*')
    .single()
  if (error) throw error
  return { event: mapWorkspaceEvent(data) }
}

export async function rewindWorkspaceConversation(
  conversationId: number | string,
  request: WorldConversationRewindRequest,
): Promise<WorldConversationRewindResponse> {
  const { data, error } = await supabase.rpc('rpc_rewind_workspace_conversation_v1', {
    p_conversation_id: Number(conversationId),
    p_turn_id: request.turn_id,
  })
  if (error) throw error
  const payload = toRecord(data)
  return {
    conversation: mapWorkspaceConversation(payload.conversation)!,
    messages: toRecordArray(payload.messages).map(mapWorkspaceMessage),
    events: toRecordArray(payload.events).map(mapWorkspaceEvent),
  }
}
